// Admin console — a tabbed dashboard (admin-only; route wrapped in <RequireAdmin>,
// backend enforces admin on every call too). Tabs: Overview (editable project
// notebook), System readiness (auto health checks), Users (tiers + invites),
// Costs (spend across all APIs/users), Field engines (specialist-solver curation).
// The active tab is mirrored to ?tab= so it survives refresh / deep-links, and
// only the active tab's panel mounts, so each panel's queries fire lazily.
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createInvite,
  deepenFieldEngine,
  deleteInvite,
  listFieldEngines,
  listInvites,
  listUsers,
  setFieldEngineStatus,
  setUserTier,
  type AdminUser,
  type FieldEngine,
  type FieldEngineStatus,
} from "@/api/admin";
import { Alert, Button, Input, Spinner, Tabs, cx, type TabItem } from "@/components/ui";
import { AdminReadinessSection } from "./admin-readiness-section";
import { AdminCostsSection } from "./admin-costs-section";
import { AdminNotebookSection } from "./admin-notebook-section";

const BETA = "beta";
// Signup default since the App Store launch: LLM enabled but quota-capped.
// "Revoke" drops a beta tester back to it (the no-LLM "test" tier is legacy).
const FREE = "free";

type TabKey = "overview" | "readiness" | "users" | "costs" | "field-engines";
const TAB_KEYS: TabKey[] = ["overview", "readiness", "users", "costs", "field-engines"];

function tierBadge(tier: string): string {
  if (tier === BETA) return "bg-emerald-50 text-emerald-700 ring-emerald-200";
  if (tier === FREE) return "bg-sky-50 text-sky-700 ring-sky-200";
  return "bg-black/5 text-ink-soft ring-black/10";
}

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

export function AdminPage() {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  const raw = params.get("tab");
  const active: TabKey = (TAB_KEYS as string[]).includes(raw ?? "")
    ? (raw as TabKey)
    : "overview";

  const tabs: TabItem[] = [
    { key: "overview", label: t("Overview") },
    { key: "readiness", label: t("System readiness") },
    { key: "users", label: t("Users") },
    { key: "costs", label: t("Costs") },
    { key: "field-engines", label: t("Field engines") },
  ];

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-ink">{t("Admin")}</h1>
        <p className="mt-1 text-sm text-ink-faint">
          {t("Project overview, system health, users, and costs — all in one place.")}
        </p>
      </div>

      <Tabs
        items={tabs}
        value={active}
        onChange={(key) =>
          setParams(
            (prev) => {
              prev.set("tab", key);
              return prev;
            },
            { replace: true },
          )
        }
      />

      {active === "overview" && <AdminNotebookSection />}
      {active === "readiness" && <AdminReadinessSection />}
      {active === "users" && <UsersSection />}
      {active === "costs" && <AdminCostsSection />}
      {active === "field-engines" && <FieldEnginesSection />}
    </div>
  );
}

function UsersSection() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const usersQ = useQuery({ queryKey: ["admin-users"], queryFn: listUsers });
  const invitesQ = useQuery({ queryKey: ["admin-invites"], queryFn: listInvites });
  const [search, setSearch] = useState("");
  const [copied, setCopied] = useState(false);
  const [lastLink, setLastLink] = useState<string | null>(null);

  const tierMut = useMutation({
    mutationFn: ({ id, tier }: { id: string; tier: string }) => setUserTier(id, tier),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-users"] }),
  });

  const inviteMut = useMutation({
    mutationFn: () => createInvite({ grants_tier: BETA }),
    onSuccess: (res) => {
      setLastLink(res.link);
      setCopied(false);
      void navigator.clipboard?.writeText(res.link).then(() => setCopied(true));
      qc.invalidateQueries({ queryKey: ["admin-invites"] });
    },
  });

  const deleteInviteMut = useMutation({
    mutationFn: (id: string) => deleteInvite(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-invites"] }),
  });

  const filtered = useMemo(() => {
    const users = usersQ.data ?? [];
    const q = search.trim().toLowerCase();
    if (!q) return users;
    return users.filter(
      (u) =>
        u.email.toLowerCase().includes(q) ||
        (u.display_name ?? "").toLowerCase().includes(q),
    );
  }, [usersQ.data, search]);

  return (
    <div className="space-y-6">
      {/* Invites */}
      <div className="card p-5">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h2 className="text-sm font-semibold text-ink">{t("Invite links")}</h2>
            <p className="mt-1 text-sm text-ink-faint">
              {t("Create a one-time link that grants beta access, then email it to a tester.")}
            </p>
          </div>
          <Button onClick={() => inviteMut.mutate()} loading={inviteMut.isPending}>
            {t("Create invite link")}
          </Button>
        </div>

        {inviteMut.isError && <Alert>{t("Couldn't create the invite.")}</Alert>}
        {lastLink && (
          <div className="mt-3 flex items-center gap-2 rounded-lg bg-brand-50 p-3">
            <code className="min-w-0 flex-1 truncate text-xs text-brand-700">{lastLink}</code>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                void navigator.clipboard?.writeText(lastLink).then(() => setCopied(true));
              }}
            >
              {copied ? t("Copied") : t("Copy")}
            </Button>
          </div>
        )}

        {/* Outstanding / used invites */}
        <div className="mt-4">
          {invitesQ.isLoading ? (
            <div className="flex justify-center py-4 text-brand-600">
              <Spinner className="h-5 w-5" />
            </div>
          ) : (invitesQ.data ?? []).length === 0 ? (
            <p className="text-sm text-ink-faint">{t("No invites yet.")}</p>
          ) : (
            <div className="card divide-y divide-black/5 p-0">
              {(invitesQ.data ?? []).map((inv) => (
                <div key={inv.id} className="flex items-center gap-3 px-4 py-2.5 text-sm">
                  <span
                    className={cx(
                      "shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ring-1",
                      inv.status === "used"
                        ? "bg-emerald-50 text-emerald-700 ring-emerald-200"
                        : inv.status === "expired"
                          ? "bg-red-50 text-red-700 ring-red-200"
                          : "bg-amber-50 text-amber-700 ring-amber-200",
                    )}
                  >
                    {t(inv.status)}
                  </span>
                  <span className="min-w-0 flex-1 truncate text-ink">
                    {inv.used_by_email ?? t("not redeemed")}
                  </span>
                  <span className="shrink-0 text-xs text-ink-faint">
                    {t("expires")} {fmtDate(inv.expires_at)}
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={deleteInviteMut.isPending}
                    onClick={() => {
                      if (window.confirm(t("Delete this invite link?"))) {
                        deleteInviteMut.mutate(inv.id);
                      }
                    }}
                  >
                    {t("Delete")}
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Users */}
      <div className="card p-5">
        <div className="flex items-center justify-between gap-3">
          <h2 className="text-sm font-semibold text-ink">{t("Users")}</h2>
          <div className="w-56">
            <Input
              label=""
              placeholder={t("Search email or name")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>

        {usersQ.isError && <Alert>{t("Couldn't load users.")}</Alert>}
        {tierMut.isError && <Alert>{t("Couldn't update that user.")}</Alert>}

        {usersQ.isLoading ? (
          <div className="flex justify-center py-8 text-brand-600">
            <Spinner className="h-6 w-6" />
          </div>
        ) : (
          <div className="mt-3 card divide-y divide-black/5 p-0">
            {filtered.map((u) => (
              <UserRow key={u.id} user={u} busy={tierMut.isPending} onSetTier={tierMut.mutate} />
            ))}
            {filtered.length === 0 && (
              <p className="px-4 py-3 text-sm text-ink-faint">{t("No matching users.")}</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function fieldStatusBadge(status: FieldEngineStatus): string {
  switch (status) {
    case "pinned":
      return "bg-violet-50 text-violet-700 ring-violet-200";
    case "reviewed":
      return "bg-emerald-50 text-emerald-700 ring-emerald-200";
    default:
      return "bg-black/5 text-ink-soft ring-black/10";
  }
}

function FieldEnginesSection() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const enginesQ = useQuery({ queryKey: ["admin-field-engines"], queryFn: listFieldEngines });
  const [search, setSearch] = useState("");

  const statusMut = useMutation({
    mutationFn: ({ path, status }: { path: string; status: FieldEngineStatus }) =>
      setFieldEngineStatus(path, status),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-field-engines"] }),
  });
  const deepenMut = useMutation({
    mutationFn: (path: string) => deepenFieldEngine(path),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-field-engines"] }),
  });

  const filtered = useMemo(() => {
    const engines = enginesQ.data ?? [];
    const q = search.trim().toLowerCase();
    if (!q) return engines;
    return engines.filter(
      (e) => e.path.toLowerCase().includes(q) || (e.label ?? "").toLowerCase().includes(q),
    );
  }, [enginesQ.data, search]);

  const candidates = useMemo(
    () => (enginesQ.data ?? []).filter((e) => e.candidate).length,
    [enginesQ.data],
  );

  return (
    <div className="card p-5">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold text-ink">{t("Field engines")}</h2>
          <p className="mt-1 text-sm text-ink-faint">
            {t(
              "Specialist solver engines the catalog has grown, by usage. Promote to lock a profile, or deepen to auto-author specialist-grade prompts.",
            )}
            {candidates > 0 && (
              <span className="ml-1 font-medium text-amber-700">
                {t("{{count}} candidate(s) to review", { count: candidates })}
              </span>
            )}
          </p>
        </div>
        <div className="w-56 shrink-0">
          <Input
            label=""
            placeholder={t("Search field or path")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      {enginesQ.isError && <Alert>{t("Couldn't load field engines.")}</Alert>}
      {statusMut.isError && <Alert>{t("Couldn't update that engine.")}</Alert>}
      {deepenMut.isError && <Alert>{t("Couldn't deepen that engine.")}</Alert>}

      {enginesQ.isLoading ? (
        <div className="flex justify-center py-8 text-brand-600">
          <Spinner className="h-6 w-6" />
        </div>
      ) : (
        <div className="mt-3 card divide-y divide-black/5 p-0">
          {filtered.map((e) => (
            <FieldEngineRow
              key={e.path}
              engine={e}
              busy={statusMut.isPending || deepenMut.isPending}
              deepeningPath={deepenMut.isPending ? deepenMut.variables : undefined}
              onSetStatus={statusMut.mutate}
              onDeepen={deepenMut.mutate}
            />
          ))}
          {filtered.length === 0 && (
            <p className="px-4 py-3 text-sm text-ink-faint">{t("No field engines yet.")}</p>
          )}
        </div>
      )}
    </div>
  );
}

function FieldEngineRow({
  engine,
  busy,
  deepeningPath,
  onSetStatus,
  onDeepen,
}: {
  engine: FieldEngine;
  busy: boolean;
  deepeningPath: string | undefined;
  onSetStatus: (v: { path: string; status: FieldEngineStatus }) => void;
  onDeepen: (path: string) => void;
}) {
  const { t } = useTranslation();
  // Promotion ladder: auto → reviewed → pinned.
  const nextStatus: FieldEngineStatus | null =
    engine.status === "auto" ? "reviewed" : engine.status === "reviewed" ? "pinned" : null;
  return (
    <div className="flex items-center gap-3 px-4 py-2.5">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="truncate text-sm font-medium text-ink">
            {engine.label || engine.path}
          </span>
          {engine.has_deep && (
            <span className="shrink-0 rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold uppercase text-emerald-700 ring-1 ring-emerald-200">
              {t("specialist")}
            </span>
          )}
          {engine.candidate && (
            <span className="shrink-0 rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-semibold uppercase text-amber-700 ring-1 ring-amber-200">
              {t("candidate")}
            </span>
          )}
        </div>
        <div className="truncate text-xs text-ink-faint">
          {engine.path} · {engine.origin} · {t("{{count}} use(s)", { count: engine.uses })}
        </div>
      </div>
      <span
        className={cx(
          "shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ring-1",
          fieldStatusBadge(engine.status),
        )}
      >
        {engine.status}
      </span>
      {!engine.has_deep && engine.origin !== "specialist" && (
        <Button
          variant="secondary"
          size="sm"
          disabled={busy}
          loading={deepeningPath === engine.path}
          onClick={() => {
            if (window.confirm(t("Auto-author specialist-grade prompts for this field?"))) {
              onDeepen(engine.path);
            }
          }}
        >
          {t("Deepen")}
        </Button>
      )}
      {nextStatus && (
        <Button
          variant="primary"
          size="sm"
          disabled={busy}
          onClick={() => onSetStatus({ path: engine.path, status: nextStatus })}
        >
          {nextStatus === "reviewed" ? t("Allow") : t("Pin")}
        </Button>
      )}
    </div>
  );
}

function UserRow({
  user,
  busy,
  onSetTier,
}: {
  user: AdminUser;
  busy: boolean;
  onSetTier: (v: { id: string; tier: string }) => void;
}) {
  const { t } = useTranslation();
  const isBeta = user.account_tier === BETA;
  return (
    <div className="flex items-center gap-3 px-4 py-2.5">
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-medium text-ink">
          {user.display_name || user.email}
        </div>
        <div className="truncate text-xs text-ink-faint">{user.email}</div>
      </div>
      <span className="shrink-0 text-xs text-ink-faint">{fmtDate(user.created_at)}</span>
      <span
        className={cx(
          "shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ring-1",
          tierBadge(user.account_tier),
        )}
      >
        {user.account_tier}
      </span>
      <Button
        variant={isBeta ? "ghost" : "primary"}
        size="sm"
        disabled={busy}
        onClick={() => onSetTier({ id: user.id, tier: isBeta ? FREE : BETA })}
      >
        {isBeta ? t("Revoke") : t("Grant beta")}
      </Button>
    </div>
  );
}
